/* Native Shopify cart transport. Prices and discounts always come from Shopify. */
(() => {
  if (window.ToyboardCart) return;
  let busy = false;
  let refreshVersion = 0;
  let opener = null;
  const disabledStates = new WeakMap();
  const root = () => window.Shopify?.routes?.root || '/';
  const cartURL = () => root() + 'cart';
  const drawer = () => document.querySelector('.cart-sidebar');
  const errorMessage = 'Modification non confirmée. Vérifiez votre panier avant de réessayer.';

  function showError(message, scope = drawer() || document) {
    const target = scope.querySelector('[data-cart-error]');
    if (target) {
      target.textContent = message;
      target.hidden = !message;
    }
  }

  function lockControl(control, locked) {
    if (locked) {
      if (!disabledStates.has(control)) disabledStates.set(control, control.disabled);
      control.disabled = true;
    } else if (disabledStates.has(control)) {
      control.disabled = disabledStates.get(control);
      disabledStates.delete(control);
    }
  }

  function applyInterlock(scope = document, inheritedStale = false) {
    const surfaces = Array.from(scope.querySelectorAll('[data-cart-surface], toyboard-cart-product'));
    if (scope.matches?.('[data-cart-surface], toyboard-cart-product')) surfaces.push(scope);
    surfaces.forEach(surface => surface.setAttribute('aria-busy', String(busy)));
    scope.querySelectorAll('button, input, select, textarea').forEach(control => {
      if (!control.closest('[data-cart-surface], toyboard-cart-product') || control.matches('.cart-drawer-close, .drawer__close-btn')) return;
      lockControl(control, busy || inheritedStale || Boolean(control.closest('[data-cart-stale="true"]')));
    });
  }

  function setBusy(value) {
    busy = value;
    applyInterlock();
    if (!value) document.querySelectorAll('toyboard-cart-product').forEach(card => card.syncAvailability?.());
  }

  function markPageStale() {
    document.querySelectorAll('.section-cart[data-cart-surface]').forEach(surface => {
      surface.dataset.cartStale = 'true';
      showError('Le contenu de cette page doit être vérifié. Actualisez le panier avant de poursuivre.', surface);
      if (!surface.querySelector('[data-cart-recovery]')) {
        const link = document.createElement('a');
        link.href = cartURL(); link.dataset.cartRecovery = ''; link.className = 'button';
        link.textContent = 'Actualiser le panier';
        surface.querySelector('[data-cart-error]')?.after(link);
      }
    });
    applyInterlock();
  }

  async function request(path, payload) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(root() + path, {
        method: 'POST', credentials: 'same-origin', signal: controller.signal,
        headers: payload instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: payload instanceof FormData ? payload : JSON.stringify(payload)
      });
      let data;
      try { data = await response.json(); } catch { throw new Error(errorMessage); }
      if (!response.ok || data.status) {
        const error = new Error(typeof data.description === 'string' ? data.description : errorMessage);
        error.rejected = response.status === 422;
        throw error;
      }
      if (path === 'cart/add.js') {
        const added = Array.isArray(data.items) ? data.items : [data];
        if (!added.length || !added.every(item => item && /^\d+$/.test(String(item.id)) && Number.isInteger(item.quantity) && item.quantity > 0)) throw new Error(errorMessage);
      } else if (!Array.isArray(data.items)) {
        throw new Error(errorMessage);
      }
      return data;
    } catch (error) {
      if (error.name === 'TypeError' || error.name === 'AbortError') throw new Error(errorMessage);
      throw error;
    } finally { clearTimeout(timeout); }
  }

  function open() {
    if (!drawer()) return;
    opener = document.activeElement;
    drawer().inert = false;
    drawer().setAttribute('role', 'dialog');
    drawer().setAttribute('aria-modal', 'true');
    drawer().setAttribute('aria-label', 'Panier');
    document.body.classList.remove('open-menu', 'open-search', 'open-sort', 'open-filter');
    document.body.classList.add('open-drawer');
    drawer().querySelector('.cart-drawer-close')?.focus();
  }

  function close() {
    document.body.classList.remove('open-drawer');
    if (drawer()) { drawer().inert = true; drawer().removeAttribute('aria-modal'); }
    if (opener?.isConnected) opener.focus();
  }

  // Discount state comes from the native Ajax cart, never from calculated client prices.
  const discounts = { codes: [], draft: '', message: '', tone: '', loaded: false };
  function nativeCodes(cart) {
    if (!Array.isArray(cart.discount_codes)) return null;
    if (!cart.discount_codes.every(entry => entry && typeof entry.code === 'string' && typeof entry.applicable === 'boolean')) throw new Error(errorMessage);
    return cart.discount_codes.map(entry => ({ code: entry.code, applicable: entry.applicable }));
  }
  async function readNativeCart() {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(cartURL() + '.js', { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json' } });
      const cart = await response.json();
      if (!response.ok || !Array.isArray(cart.items)) throw new Error(errorMessage);
      nativeCodes(cart);
      return cart;
    } finally { clearTimeout(timeout); }
  }
  function syncDiscountUI() {
    const section = drawer()?.querySelector('[data-cart-discount]');
    if (!section) return;
    const input = section.querySelector('[data-discount-input]');
    if (input) input.value = discounts.draft;
    const list = section.querySelector('[data-discount-list]');
    if (list) {
      list.replaceChildren();
      discounts.codes.forEach(entry => {
        const line = document.createElement('li');
        const state = document.createElement('span'); state.dataset.discountState = ''; state.dataset.applicable = String(entry.applicable);
        const code = document.createElement('span'); code.dataset.discountCode = ''; code.textContent = entry.code;
        state.append(code, document.createTextNode(entry.applicable ? ' : appliqué' : ' : non applicable au panier'));
        const remove = document.createElement('button'); remove.type = 'button'; remove.dataset.discountRemove = ''; remove.dataset.code = entry.code;
        remove.setAttribute('aria-label', 'Retirer le code ' + entry.code); remove.textContent = 'Retirer';
        line.append(state, remove); list.append(line);
      });
      list.hidden = discounts.codes.length === 0;
    }
    const message = section.querySelector('[data-discount-message]');
    if (message) { message.textContent = discounts.message; message.hidden = !discounts.message; message.dataset.tone = discounts.tone; }
    applyInterlock(section);
  }
  function discountMessage(message, tone = '') {
    discounts.message = message; discounts.tone = tone; syncDiscountUI();
  }
  async function updateDiscount(code, removing = false) {
    if (busy || drawer()?.dataset.cartStale === 'true' || !drawer()?.querySelector('[data-cart-discount]')) return false;
    code = String(code).trim();
    const requested = removing ? [code] : code.split(',').map(value => value.trim()).filter(Boolean);
    if (!requested.length || !code) { discountMessage('Saisissez un code de réduction.', 'error'); drawer()?.querySelector('[data-discount-input]')?.focus(); return false; }
    setBusy(true); discountMessage('Vérification du code…');
    let sent = false;
    try {
      // Read before replacing the complete code set, keeping all other current codes.
      const current = nativeCodes(await readNativeCart());
      if (!current) throw new Error('Les codes du panier n’ont pas pu être vérifiés. Réessayez après avoir actualisé le panier.');
      let codes = current.map(entry => entry.code);
      if (removing) codes = codes.filter(value => value.toLocaleLowerCase() !== code.toLocaleLowerCase());
      else requested.forEach(value => { if (!codes.some(existing => existing.toLocaleLowerCase() === value.toLocaleLowerCase())) codes.push(value); });
      sent = true;
      const result = await request('cart/update.js', { discount: codes.join(',') });
      if (!nativeCodes(result)) throw new Error(errorMessage);
      markPageStale();
      if (!(await refresh())) { discountMessage('Le panier n’a pas pu être actualisé. Vérifiez-le avant de poursuivre.', 'error'); return false; }
      const confirmed = removing ? !discounts.codes.some(entry => entry.code.toLocaleLowerCase() === code.toLocaleLowerCase()) : requested.every(value => discounts.codes.some(entry => entry.code.toLocaleLowerCase() === value.toLocaleLowerCase() && entry.applicable));
      const stored = !removing && requested.every(value => discounts.codes.some(entry => entry.code.toLocaleLowerCase() === value.toLocaleLowerCase()));
      if (stored || (removing && confirmed && discounts.draft.trim().toLocaleLowerCase() === code.toLocaleLowerCase())) discounts.draft = '';
      if (confirmed) {
        discountMessage(removing ? 'Code retiré.' : 'Code appliqué. Il est conservé pour le paiement.', 'success');
      } else {
        discountMessage(removing ? 'Le retrait du code n’a pas été confirmé.' : 'Code non applicable à ce panier. Les conditions et les cumuls, notamment la livraison, sont vérifiés par Shopify au paiement.', 'error');
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: result } }));
      // A saved shipping/customer code can become eligible at checkout. Do not block payment merely because it is not applicable in this cart yet.
      return removing ? confirmed : stored;
    } catch (error) {
      if (error.rejected || !sent) {
        discountMessage(error.message || errorMessage, 'error');
      } else {
        // An ambiguous reply may already have committed. Read back, never repeat the POST.
        markPageStale();
        const verified = await refresh();
        discountMessage('Modification non confirmée.' + (verified ? ' Le panier a été relu. Vérifiez les codes affichés.' : ' Actualisez le panier avant de poursuivre.'), 'error');
      }
      return false;
    } finally { setBusy(false); }
  }
  document.addEventListener('input', event => {
    if (!event.target.matches?.('#cart-drawer [data-discount-input]')) return;
    discounts.draft = event.target.value;
    discounts.message = ''; discounts.tone = '';
    const message = event.target.closest('[data-cart-discount]')?.querySelector('[data-discount-message]');
    if (message) { message.textContent = ''; message.hidden = true; }
  });
  document.addEventListener('click', event => {
    const apply = event.target.closest('#cart-drawer [data-discount-apply]');
    const remove = event.target.closest('#cart-drawer [data-discount-remove]');
    if (!apply && !remove) return;
    event.preventDefault();
    if ((apply || remove).disabled) return;
    updateDiscount(remove ? remove.dataset.code : discounts.draft, Boolean(remove));
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Enter' || !event.target.matches?.('#cart-drawer [data-discount-input]')) return;
    event.preventDefault();
    if (!event.target.disabled) updateDiscount(event.target.value);
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!form.matches?.('#CartDrawerForm') || event.defaultPrevented || event.submitter?.name !== 'checkout') return;
    const input = form.querySelector('[data-discount-input]');
    if (!input || !input.value.trim()) return;
    event.preventDefault();
    const transferred = await updateDiscount(input.value);
    if (transferred) {
      const current = drawer()?.querySelector('#CartDrawerForm');
      const checkout = current?.querySelector('[name="checkout"]');
      if (checkout && !checkout.disabled && !busy && drawer()?.dataset.cartStale !== 'true') current.requestSubmit(checkout);
    } else {
      drawer()?.querySelector('[data-discount-input]')?.focus();
    }
  });

  async function refresh() {
    if (!drawer()) return false;
    const version = ++refreshVersion;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(cartURL() + '?section_id=cart-drawer', { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error(errorMessage);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = html.querySelector('#cart-drawer[data-item-count]');
      if (!source || !/^\d+$/.test(source.dataset.itemCount)) throw new Error(errorMessage);
      const native = source.querySelector('[data-cart-discount]') ? await readNativeCart() : null;
      if (native && Number(native.item_count) !== Number(source.dataset.itemCount)) throw new Error(errorMessage);
      if (version !== refreshVersion) return false;
      const nextCodes = native ? (nativeCodes(native) || []) : [];
      if (JSON.stringify(nextCodes) !== JSON.stringify(discounts.codes)) { discounts.message = ''; discounts.tone = ''; }
      discounts.codes = nextCodes; discounts.loaded = Boolean(native);
      const focused = document.activeElement;
      const focusInside = drawer().contains(focused);
      const focusedKey = focused?.closest('[data-line-key]')?.dataset.lineKey;
      const focusedClass = focused?.classList.contains('ci_plus') ? '.ci_plus' : focused?.classList.contains('ci_minus') ? '.ci_minus' : null;
      delete drawer().dataset.cartStale;
      // Lock detached nodes before publishing them, retaining their native states.
      const incoming = document.importNode(source, true);
      applyInterlock(incoming);
      drawer().replaceChildren(incoming);
      syncDiscountUI();
      drawer().querySelectorAll('.cart-addons-drawer').forEach(panel => { panel.inert = !panel.hasAttribute('open'); panel.setAttribute('aria-hidden', String(panel.inert)); });
      drawer().inert = !document.body.classList.contains('open-drawer');
      document.querySelectorAll('.cart-count').forEach((count) => { count.textContent = Number(source.dataset.itemCount) ? source.dataset.itemCount : ''; });
      if (typeof window.ShippingCalculator === 'function') window.ShippingCalculator();
      if (typeof window.recentlyViewedProducts === 'function') window.recentlyViewedProducts();
      applyInterlock();
      if (focusedKey && focusedClass) {
        const row = Array.from(drawer().querySelectorAll('[data-line-key]')).find((node) => node.dataset.lineKey === focusedKey);
        (row?.querySelector(focusedClass) || drawer().querySelector('.cart-drawer-close'))?.focus();
      } else if (focusInside && document.body.classList.contains('open-drawer')) {
        drawer().querySelector('.cart-drawer-close')?.focus();
      }
      document.dispatchEvent(new CustomEvent('cart:refreshed', { detail: { item_count: Number(source.dataset.itemCount) } }));
      return true;
    } catch {
      if (version !== refreshVersion) return false;
      drawer().dataset.cartStale = 'true';
      if (!drawer().querySelector('[data-cart-error]')) {
        const shell = document.createElement('div'); shell.id = 'cart-drawer';
        const closeButton = document.createElement('button'); closeButton.type = 'button'; closeButton.className = 'cart-drawer-close'; closeButton.setAttribute('aria-label', 'Fermer le panier'); closeButton.textContent = '×';
        const p = document.createElement('p'); p.dataset.cartError = ''; p.setAttribute('role', 'alert');
        const link = document.createElement('a'); link.href = cartURL(); link.textContent = 'Voir le panier';
        shell.append(closeButton, p, link); drawer().replaceChildren(shell);
      }
      applyInterlock();
      showError('Le panier n’a pas pu être actualisé. Ouvrez la page panier pour vérifier son contenu.');
      return false;
    } finally { clearTimeout(timeout); }
  }

  async function change(key, quantity, scope = drawer()) {
    if (busy || drawer()?.dataset.cartStale === 'true' || !key || !Number.isInteger(Number(quantity)) || Number(quantity) < 0) return false;
    setBusy(true); showError('', scope);
    try {
      const data = await request('cart/change.js', { id: String(key), quantity: Number(quantity) });
      markPageStale();
      if (!(await refresh())) {
        if (drawer()) drawer().dataset.cartStale = 'true';
        showError('Le panier n’a pas pu être actualisé. Ouvrez la page panier pour vérifier son contenu.');
        return false;
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
      return true;
    } catch (error) {
      if (error.rejected) {
        (drawer() || scope)?.querySelectorAll('.cart_qty').forEach(input => { input.value = input.defaultValue; });
        showError(error.message || errorMessage, drawer() || scope);
      } else {
        // A lost response is not a rollback. Never retry the ambiguous POST.
        markPageStale();
        const verified = await refresh();
        if (!verified && drawer()) drawer().dataset.cartStale = 'true';
        showError(errorMessage + (verified ? ' Le contenu affiché a été relu auprès du panier.' : ' Ouvrez la page panier pour vérifier son contenu.'), drawer() || scope);
      }
      return false;
    } finally { setBusy(false); }
  }

  async function afterAdd() {
    if (!drawer()) {
      window.location.assign(cartURL());
      return true;
    }
    const ok = await refresh();
    open();
    return ok;
  }

  async function add(id, quantity, scope) {
    if (busy || scope?.dataset.cartUncertain === 'true' || scope?.closest('[data-cart-stale="true"]') || !/^\d+$/.test(String(id)) || !Number.isInteger(quantity) || quantity < 1) return false;
    setBusy(true); showError('', scope);
    try {
      await request('cart/add.js', { items: [{ id: String(id), quantity }] });
      markPageStale();
      return await afterAdd();
    } catch (error) {
      if (error.rejected) showError(error.message || errorMessage, scope);
      else {
        if (scope) scope.dataset.cartUncertain = 'true';
        markPageStale();
        const verified = await refresh();
        showError(errorMessage + (verified ? ' Le contenu affiché a été relu auprès du panier.' : ''), drawer() || scope);
      }
      return false;
    } finally { setBusy(false); }
  }


  // Enhance only the validated native PDP form, after its variant and accessory handlers.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.matches?.('.tb-native-product-form') || !drawer()) return;
    if (event.defaultPrevented) return;
    const product = form.closest('tb-product');
    const button = form.querySelector('[name="add"]');
    const variant = form.querySelector('[name="id"]');
    event.preventDefault();
    if (busy || form.dataset.cartSubmitting === 'true' || form.dataset.cartUncertain === 'true') return;
    if (!product || product.busy || product.hasAttribute('data-variant-error') || !variant || variant.disabled || !/^\d+$/.test(variant.value) || !button || button.disabled || !form.reportValidity()) return;
    const errorBox = form.querySelector('.product-form-error');
    if (errorBox) errorBox.textContent = '';
    // FormData fires the existing upsell hook while the native inputs are still enabled.
    const payload = new FormData(form);
    payload.delete('return_to'); payload.delete('redirect_to');
    const firstId = payload.get('id') || payload.get('items[0][id]');
    const quantity = Number(payload.get('quantity') || payload.get('items[0][quantity]') || 1);
    if (String(firstId) !== variant.value || !Number.isInteger(quantity) || quantity < 1) return;
    form.dataset.cartSubmitting = 'true'; form.setAttribute('aria-busy', 'true');
    setBusy(true); lockControl(button, true);
    try {
      await request('cart/add.js', payload);
      form.closest('quick-add-modal')?.hide();
      markPageStale();
      await afterAdd();
    } catch (error) {
      if (error.rejected) {
        if (errorBox) errorBox.textContent = error.message || errorMessage;
      } else {
        // Never resubmit a POST whose reply was lost. Read back and ask for verification.
        form.dataset.cartUncertain = 'true';
        if (errorBox) errorBox.textContent = errorMessage;
        await refresh(); open(); showError(errorMessage);
      }
    } finally {
      setBusy(false); lockControl(button, false);
      delete form.dataset.cartSubmitting; form.removeAttribute('aria-busy');
      if (form.dataset.cartUncertain === 'true' || product.busy || product.hasAttribute('data-variant-error')) button.disabled = true;
    }
  });

  // Capture only the homepage bag before the legacy direct .pv_add listener.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.pv_product[data-home-cart-product] .pv_add[data-home-cart-add]');
    if (!button) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const scope = button.closest('[data-home-cart-product]');
    if (busy || scope.dataset.available !== 'true' || scope.getAttribute('aria-busy') === 'true') return;
    scope.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    try { await add(button.dataset.id, 1, scope); }
    finally {
      scope.setAttribute('aria-busy', 'false');
      button.setAttribute('aria-disabled', String(scope.dataset.available !== 'true'));
    }
  }, true);

  function closeAddon(target, restoreFocus = false) {
    if (!target) return;
    target.classList.remove('drawer--active'); target.removeAttribute('open'); target.style.maxHeight = '0px';
    target.inert = true; target.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('.cart-addons-tab').forEach((tab) => {
      if (tab.getAttribute('aria-controls') === target.id) { tab.setAttribute('aria-expanded', 'false'); if (restoreFocus && tab.isConnected) tab.focus({ preventScroll: true }); }
    });
  }

  document.addEventListener('submit', (event) => {
    const surface = event.target.closest('[data-cart-surface]');
    if (surface && (busy || surface.closest('[data-cart-stale="true"]'))) event.preventDefault();
  }, true);

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-cart-surface] .item-remove');
    if (remove && (busy || remove.closest('[data-cart-stale="true"]'))) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('click', async (event) => {
    const header = event.target.closest('.header-cart');
    if (header && drawer()) { event.preventDefault(); open(); await refresh(); return; }
    if (event.target.closest('.cart-drawer-close, [data-cart-continue]') || (document.body.classList.contains('open-drawer') && event.target.closest('.overlay-box'))) {
      event.preventDefault(); close(); return;
    }
    const surface = event.target.closest('#cart-drawer');
    if (!surface) return;
    const remove = event.target.closest('.item-remove[data-line-key]');
    if (remove) { event.preventDefault(); if (!busy) await change(remove.dataset.lineKey, 0, surface); return; }
    const stepper = event.target.closest('.ci_plus, .ci_minus');
    if (stepper) {
      event.preventDefault(); if (busy) return;
      const row = stepper.closest('.item-row'); const input = row.querySelector('.cart_qty');
      const quantity = Number(input.value) + (stepper.classList.contains('ci_plus') ? 1 : -1);
      if (quantity >= 0) await change(row.dataset.lineKey, quantity, surface);
      return;
    }
    const tab = event.target.closest('.chead-tab');
    if (tab) {
      surface.querySelectorAll('.chead-tab').forEach((node) => { const active = node === tab; node.classList.toggle('active', active); node.setAttribute('aria-selected', String(active)); });
      surface.querySelectorAll('.cart-tab-list').forEach((node) => node.classList.toggle('active', node.dataset.id === tab.dataset.id));
    }
    const addon = event.target.closest('.cart-addons-tab');
    if (addon) {
      const target = document.getElementById(addon.getAttribute('aria-controls'));
      if (!target) return;
      const wasOpen = target.classList.contains('drawer--active');
      surface.querySelectorAll('.cart-addons-drawer').forEach(panel => closeAddon(panel));
      if (!wasOpen) { target.inert = false; target.setAttribute('aria-hidden', 'false'); target.classList.add('drawer--active'); target.setAttribute('open', ''); target.style.maxHeight = target.scrollHeight + 'px'; addon.setAttribute('aria-expanded', 'true'); target.querySelector('textarea,select,input,.drawer__close-btn')?.focus({ preventScroll: true }); }
    }
    const closeButton = event.target.closest('.drawer__close-btn, .fixed-overlay');
    if (closeButton) closeAddon(closeButton.closest('.cart-addons-drawer'), true);
    const noteButton = event.target.closest('.cart-note-submit');
    if (noteButton && !busy) {
      const noteDrawer = noteButton.closest('.cart-addons-drawer');
      const note = noteDrawer.querySelector('textarea[name="note"]');
      setBusy(true); showError('', surface);
      try { await request('cart/update.js', { note: note.value }); closeAddon(noteDrawer, true); }
      catch (error) { showError(error.message || errorMessage, surface); }
      finally { setBusy(false); }
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('#cart-drawer .cart_qty');
    if (!input || busy) return;
    if (!input.checkValidity()) { input.reportValidity(); input.value = input.defaultValue; return; }
    change(input.closest('.item-row').dataset.lineKey, Number(input.value), input.closest('#cart-drawer'));
  });

  document.addEventListener('keydown', (event) => {
    if (!document.body.classList.contains('open-drawer') || !drawer()) return;
    const addonPanel = drawer().querySelector('.cart-addons-drawer[open]');
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); if (addonPanel) closeAddon(addonPanel, true); else close(); return; }
    if (event.key !== 'Tab') return;
    const focusable = Array.from((addonPanel || drawer()).querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled)')).filter((el) => el.getClientRects().length && !el.closest('[inert],[hidden]') && getComputedStyle(el).visibility !== 'hidden');
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }, true);

  class CartProduct extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const button = this.querySelector('[data-cart-add]');
      const select = this.querySelector('[data-cart-variant]');
      const quantity = this.querySelector('[data-cart-quantity]');
      if (!button || !quantity) return;
      const sync = () => {
        const option = select?.selectedOptions[0];
        const data = option?.dataset || this.dataset;
        const available = data.available === 'true';
        const blocked = !available || this.dataset.cartUncertain === 'true';
        if (disabledStates.has(button)) disabledStates.set(button, blocked);
        else button.disabled = blocked;
        lockControl(button, busy || Boolean(this.closest('[data-cart-stale="true"]')));
        button.textContent = available ? 'Ajouter au panier' : (this.classList.contains('tb-drawer-card') ? 'Indisponible' : 'Épuisé');
        const stock = this.querySelector('[data-cart-stock]');
        if (stock) stock.textContent = available ? 'Disponible' : 'Épuisé';
        if (option) {
          this.querySelector('[data-cart-price]').textContent = data.price;
          this.dataset.variantId = option.value;
        }
        const compare = this.querySelector('[data-cart-compare]');
        if (compare) { compare.hidden = !data.compare; compare.textContent = data.compare || ''; }
        if (data.min) {
          quantity.min = data.min; quantity.step = data.step || '1';
          if (data.max) quantity.max = data.max; else quantity.removeAttribute('max');
          if (!quantity.checkValidity()) quantity.value = data.min;
        }
        if (data.url) this.querySelectorAll('[data-cart-product-link]').forEach(link => { link.href = data.url; });
        const image = this.querySelector('[data-cart-image]');
        if (image && data.imageSrc && image.getAttribute('src') !== data.imageSrc) {
          // Replace the responsive source together; an old srcset wins over a new src.
          image.alt = data.imageAlt || '';
          if (data.imageWidth) image.width = Number(data.imageWidth);
          if (data.imageHeight) image.height = Number(data.imageHeight);
          image.sizes = '100vw';
          if (data.imageSrcset) image.srcset = data.imageSrcset; else image.removeAttribute('srcset');
          image.src = data.imageSrc;
        } else if (image && data.imageSrcset && image.getAttribute('srcset') !== data.imageSrcset) {
          image.srcset = data.imageSrcset;
        }
        this.syncImageSize();
      };
      this.syncAvailability = sync;
      select?.addEventListener('change', sync, { signal });
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        if (button.disabled) return;
        if (this.classList.contains('tb-drawer-card') && matchMedia('(max-width:767px)').matches) quantity.value = quantity.min || '1';
        if (!quantity.checkValidity()) { quantity.reportValidity(); return; }
        await add(select?.value || this.dataset.variantId, Number(quantity.value), this);
      }, { signal });
      this.imageResize = new ResizeObserver(() => this.syncImageSize());
      const frame = this.querySelector('.tb-drawer-card__media');
      if (frame) this.imageResize.observe(frame);
      sync();
    }
    syncImageSize() {
      const image = this.querySelector('[data-cart-image]');
      if (!image) return;
      const width = Number(image.getAttribute('width')), height = Number(image.getAttribute('height'));
      if (!(width > 0 && height > 0)) return;
      const box = image.getBoundingClientRect();
      if (!box.width || !box.height) return;
      const cover = getComputedStyle(image).objectFit === 'cover';
      const projected = cover ? Math.max(box.width, box.height * width / height) : Math.min(box.width, box.height * width / height);
      const sizes = Math.ceil(projected) + 'px';
      if (image.sizes !== sizes) image.sizes = sizes;
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; this.imageResize?.disconnect(); this.imageResize = null; this.syncAvailability = null; }
  }

  class DrawerRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.track = this.querySelector('[data-drawer-track]');
      this.controls = this.querySelector('[data-drawer-controls]');
      this.previous = this.querySelector('[data-drawer-prev]');
      this.next = this.querySelector('[data-drawer-next]');
      this.position = this.querySelector('[data-drawer-position]');
      if (!this.track || !this.controls) return;
      const schedule = () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.update()); };
      this.track.addEventListener('scroll', schedule, { signal, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key) || this.controls.hidden) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') this.track.scrollTo({ left: event.key === 'Home' ? 0 : this.track.scrollWidth, behavior: 'auto' });
        else this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.resize = new ResizeObserver(schedule); this.resize.observe(this.track);
      for (const child of this.track.children) this.resize.observe(child);
      window.addEventListener('resize', schedule, { signal, passive: true });
      schedule();
    }
    update() {
      const track = this.track, max = Math.max(0, track.scrollWidth - track.clientWidth);
      this.controls.hidden = max < 3;
      this.previous.disabled = track.scrollLeft <= 2;
      this.next.disabled = track.scrollLeft >= max - 2;
      const cards = [...track.children], edge = track.getBoundingClientRect().left;
      const index = cards.reduce((best,card,i) => Math.abs(card.getBoundingClientRect().left-edge) < Math.abs(cards[best].getBoundingClientRect().left-edge) ? i : best,0);
      if (cards.length) {
        this.position.textContent = `${index + 1} / ${cards.length}`;
        // Each slide keeps its own natural height instead of reserving its neighbor's controls.
        const style = getComputedStyle(track);
        const carousel = style.gridAutoFlow === 'column';
        const scrollbar = Math.max(0, track.offsetHeight - track.clientHeight);
        const height = carousel ? Math.ceil(cards[index].getBoundingClientRect().height + (Number.parseFloat(style.paddingBottom) || 0) + scrollbar) + 'px' : '';
        if (track.style.height !== height) track.style.height = height;
      }
    }
    move(direction) {
      const card = this.track.firstElementChild;
      if (!card) return;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    disconnectedCallback() { this.events?.abort(); this.events = null; this.resize?.disconnect(); cancelAnimationFrame(this.frame); }
  }

  class Recommendations extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.load();
    }
    disconnectedCallback() { this.controller?.abort(); this.swiper?.destroy(); this.initialized = false; }
    async load() {
      if (!this.dataset.url) return;
      try {
        const response = await fetch(this.dataset.url, { credentials: 'same-origin', signal: this.controller.signal });
        if (!response.ok) throw new Error('recommendations unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.querySelector(this.localName);
        if (!source) throw new Error('recommendations missing');
        const cardSelector = this.localName === 'product-recommendations' ? 'toyboard-cart-product, [data-tb-recommendation-card]' : 'toyboard-cart-product';
        if (source.querySelector(cardSelector) || this.localName === 'product-recommendations') {
          const incoming = document.createDocumentFragment();
          incoming.append(...Array.from(source.childNodes, node => document.importNode(node, true)));
          applyInterlock(incoming, Boolean(this.closest('[data-cart-stale="true"]')));
          this.swiper?.destroy();
          this.swiper = null;
          this.replaceChildren(incoming);
          applyInterlock(this);
        }
        this.dataset.recommendationsState = this.querySelector(cardSelector) ? 'loaded' : 'empty';
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.dataset.recommendationsState = 'unavailable';
      }
      this.hidden = !this.querySelector(this.localName === 'product-recommendations' ? 'toyboard-cart-product, [data-tb-recommendation-card]' : 'toyboard-cart-product');
      this.querySelectorAll('.tb-rec-card__image--secondary').forEach(image => {
        const media = image.closest('.tb-rec-card__media');
        const markReady = () => media?.classList.toggle('is-ready', image.naturalWidth > 0);
        if (image.complete) markReady();
        else {
          image.addEventListener('load', markReady, { once: true });
          image.addEventListener('error', () => media?.classList.remove('is-ready'), { once: true });
        }
      });
      const slider = this.querySelector('.product-carousel');
      if (slider && typeof window.Swiper === 'function') {
        this.swiper = new window.Swiper(slider, { loop: slider.dataset.loop === 'true' && slider.querySelectorAll('.swiper-slide').length >= Number(slider.dataset.desktopGrid || 4) * 2, slidesPerView: 'auto', spaceBetween: 15, navigation: { nextEl: this.querySelector('.swiper-button-next'), prevEl: this.querySelector('.swiper-button-prev') } });
      }
      this.dispatchEvent(new CustomEvent('recommendations:' + (this.hidden ? 'empty' : 'loaded'), { bubbles: true }));
    }
  }

  window.ToyboardCart = { refresh, afterAdd, add, change, open, close, cartURL, get busy() { return busy; } };
  window.load_cart = refresh;
  window.updateItem = (key, quantity) => change(key, Number(quantity));
  window.removeItem = (key) => change(key, 0);
  if (!customElements.get('tb-drawer-recommendations')) customElements.define('tb-drawer-recommendations', DrawerRecommendations);
  if (!customElements.get('toyboard-cart-product')) customElements.define('toyboard-cart-product', CartProduct);
  for (const name of ['cart-complementary', 'complementary-products', 'product-recommendations']) {
    if (!customElements.get(name)) customElements.define(name, class extends Recommendations {});
  }
  function syncDrawerVisibility() {
    const panel = drawer();
    if (!panel) return;
    const visible = document.body.classList.contains('open-drawer');
    const wasInert = panel.inert;
    panel.inert = !visible;
    if (visible) {
      panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-modal', 'true'); panel.setAttribute('aria-label', 'Panier');
      if (wasInert) { opener = document.activeElement; panel.querySelector('.cart-drawer-close')?.focus(); }
    } else panel.removeAttribute('aria-modal');
  }
  const init = () => {
    if (!drawer()) return;
    syncDrawerVisibility();
    new MutationObserver(syncDrawerVisibility).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    refresh();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
